import { useCallback, useEffect, useState } from 'react'

import { useApolloClient } from '@apollo/client'
import {
  Box,
  Button,
  Colors,
  Icon,
  type MediumIcon,
  Spinner,
  Text,
  Title,
  WizardStep,
  WizardStepRenderParams,
  useOnNext,
} from '@factorialco/deprecated-design-system'
import {
  AllEmployeeGroupsDocument,
  DatevLaudsClientDataDocument,
  DatevLaudsRequestJobStatusDocument,
} from 'generated/resources/graphql'
import { groupBy } from 'lodash'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { BusinessUnitsByLegalEntity } from 'modules/payroll_integrations_datev_lauds/components/SettingsSteps/Steps/BusinessUnitsStep/BusinessUnitsByLegalEntity'
import {
  ClientDataResultProps,
  getMatchedLegalEntities,
  modifiedClientData,
} from 'modules/payroll_integrations_datev_lauds/components/SettingsSteps/Steps/helper'
import { settingsRefreshBannerTranslations } from 'modules/payroll_integrations_datev_lauds/lib/constants'
import { DatevWizardData } from 'modules/payroll_integrations_datev_lauds/lib/types'

const JOB_COMPLETED_STATUS = ['complete', 'failed']
const POLL_INTERVAL = 60000

interface IntegrationsDatevLaudsRequest {
  __typename?: 'IntegrationsDatevLaudsRequest'
  id: number
  jobUuid?: string | null
  status?: string | null
}

interface BannerProps {
  backgroundColor: Colors
  title: string
  icon: MediumIcon
  iconColor: Colors
  description: string
  showBanner?: () => void
  buttonText?: string | null
}

const SettingsInfoBanner = ({
  backgroundColor,
  title,
  icon,
  iconColor,
  description,
  showBanner,
  buttonText,
}: BannerProps) => {
  return (
    <Box
      width="s600"
      background={backgroundColor}
      paddingX="s24"
      paddingY="s16"
      borderRadius={{
        all: 'abs008',
      }}
      gap="s4"
    >
      <Box gap="s4" flexDirection="row">
        <Icon.Medium icon={icon} color={iconColor} />
        <Text.Header3>{title}</Text.Header3>
      </Box>
      <Text.Normal size="200">{description}</Text.Normal>
      {buttonText && (
        <Box paddingTop="s8">
          <Button type="secondary" onClick={showBanner}>
            {buttonText}
          </Button>
        </Box>
      )}
    </Box>
  )
}

const BusinessUnitsStep = ({ data, onDataChanged }: WizardStepRenderParams<DatevWizardData>) => {
  const [refreshData, setRefreshData] = useState(false)
  const [isError, SetIsError] = useState(false)
  const client = useApolloClient()

  const {
    access: { id: accessId },
    company,
  } = useCurrentSession()

  useOnNext(() => {
    const employeeGroupKeys = Object.keys(data.answers).filter((key) =>
      /^employee_group_\d+$/.test(key)
    )
    const mappedEmployeeGroupCount = employeeGroupKeys.map((key) => !!data.answers[key]).length

    track(trackingEvents.payrollIntegrationsDatevLauds.datevLaudsSettingsBusinessUnitsMapped),
      {
        company_id: company.id,
        access_id: accessId,
        mapped_employee_groups: mappedEmployeeGroupCount,
      }
  })

  const onBusinessUnitChanged = (employeeGroupdId: string, value: string) => {
    onDataChanged({
      ...data,
      answers: {
        ...data.answers,
        [`employee_group_${employeeGroupdId}`]: value,
      },
    })
  }

  const matchedLegalEntities = getMatchedLegalEntities(data, company)

  const fetchDatevClientDataRequestRecord = async () => {
    try {
      const queries = matchedLegalEntities.map((el) => {
        const jobUuidKey = `job_uuid_${el.id}`
        const dataAnswer = data.answers[jobUuidKey] ?? ''

        if (!dataAnswer) return

        return client.query({
          query: DatevLaudsRequestJobStatusDocument,
          variables: {
            jobUuid: dataAnswer,
          },
        })
      })
      const results = await Promise.all(queries)

      const resultItems = results.map((res) => {
        return res?.data?.integrationsDatevLauds.requestsConnection.nodes[0]
      })

      return resultItems
    } catch (error) {
      SetIsError(true)
      setRefreshData(false)
    }
  }

  const clientDataLastUpdatedDate = data.answers.client_data_last_updated_at

  const fetchDatevClientData = async (
    items: Array<IntegrationsDatevLaudsRequest | undefined>
  ): Promise<Record<string, ClientDataResultProps>> => {
    try {
      // Filter matchedLegalEntities with job records first to avoid unnecessary processing
      const entitiesWithJobs = matchedLegalEntities.filter((el) => {
        const jobUuidKey = `job_uuid_${el.id}`
        const dataAnswerJobUuid = data.answers[jobUuidKey] ?? ''
        return items.some((record) => record?.jobUuid === dataAnswerJobUuid)
      })

      // Execute queries in parallel
      const queries = entitiesWithJobs.map((el) => {
        const legalEntityKey = `legal_entity_${el.id}`
        const jobUuidKey = `job_uuid_${el.id}`

        return client.query({
          query: DatevLaudsClientDataDocument,
          variables: {
            companyId: company.id,
            accessId: accessId,
            clientId: `${data.answers.consultant_id}-${data.answers[legalEntityKey]}`,
            jobUuid: data.answers[jobUuidKey] ?? '',
            targetSystem: data.answers.payroll_software,
          },
        })
      })

      const results = await Promise.all(queries)
      setRefreshData(false)

      const resultItems: Record<string, ClientDataResultProps> = results.reduce(
        (acc: Record<string, ClientDataResultProps>, res) => {
          const node = res?.data?.integrationsDatevLauds.clientDataJobResultsConnection?.nodes[0]

          if (node?.clientId) {
            acc[node.clientId] = {
              salaryTypesOptions: node.salaryTypes ?? [],
              businessUnitsOptions: node.businessUnits ?? [],
              departmentsOptions: node.departments ?? [],
              healthInsurersOptions: node.healthInsurers ?? [],
              clientId: node.clientId,
            }
          }
          return acc
        },
        {}
      )

      return resultItems
    } catch (error) {
      SetIsError(true)
      setRefreshData(false)
      throw error
    }
  }

  // Memoized version of modifiedClientData to prevent unnecessary recalculations
  const memoizedModifiedClientData = useCallback(modifiedClientData, [])

  const updateLegalEntitiesData = useCallback(
    (clientDataOptions: Record<string, ClientDataResultProps>) => {
      const updatedData = { ...data }

      matchedLegalEntities?.forEach((legalEntity) => {
        const legalEntityKey = `legal_entity_${legalEntity.id}`
        const dataAnswer = data.answers[legalEntityKey]
        const datevClientId = `${data.answers.consultant_id}-${dataAnswer}`
        const options = clientDataOptions[datevClientId]

        const currentBusinessUnits = JSON.parse(
          data.answers[`business_units_${legalEntity.id}`] ?? '[]'
        )
        const currentSalaryTypes = JSON.parse(
          data.answers[`salary_types_${legalEntity.id}`] ?? '[]'
        )
        const currentDepartments = JSON.parse(data.answers[`departments_${legalEntity.id}`] ?? '[]')
        const currentHealthInsurers = JSON.parse(
          data.answers[`health_insurers_${legalEntity.id}`] ?? '[]'
        )

        const newBusinessUnits = options
          ? memoizedModifiedClientData(options.businessUnitsOptions)
          : currentBusinessUnits

        const newSalaryTypes = options
          ? memoizedModifiedClientData(options.salaryTypesOptions)
          : currentSalaryTypes

        const newDepartments = options
          ? memoizedModifiedClientData(options.departmentsOptions)
          : currentDepartments

        const newHealthInsurers = options ? options.healthInsurersOptions : currentHealthInsurers

        updatedData.answers[`business_units_${legalEntity.id}`] = JSON.stringify(newBusinessUnits)
        updatedData.answers[`salary_types_${legalEntity.id}`] = JSON.stringify(newSalaryTypes)
        updatedData.answers[`departments_${legalEntity.id}`] = JSON.stringify(newDepartments)
        updatedData.answers[`health_insurers_${legalEntity.id}`] = JSON.stringify(newHealthInsurers)
      })

      updatedData.answers.client_data_last_updated_at = dateToString(new Date(), 'ui')
      onDataChanged(updatedData)
    },
    [data, matchedLegalEntities, memoizedModifiedClientData, onDataChanged]
  )

  useEffect(() => {
    if (clientDataLastUpdatedDate === undefined) setRefreshData(true)

    if (!refreshData) return

    let isMounted = true

    const executePollingCycle = async () => {
      if (!isMounted) return

      try {
        const items = await fetchDatevClientDataRequestRecord()

        const validItems = items?.filter(
          (item): item is IntegrationsDatevLaudsRequest =>
            item !== undefined && JOB_COMPLETED_STATUS.includes(item.status || '')
        )

        if (validItems?.length && validItems.length === items?.length) {
          const resultItems = await fetchDatevClientData(validItems)
          clearInterval(pollingInterval)
          updateLegalEntitiesData(resultItems)
        }
      } catch (err) {
        if (isMounted) {
          SetIsError(true)
          setRefreshData(false)
        }
      }
    }

    // Initial call
    executePollingCycle()

    // Set up polling interval
    const pollingInterval = setInterval(executePollingCycle, POLL_INTERVAL)

    // Cleanup
    return () => {
      isMounted = false
      clearInterval(pollingInterval)
    }
  }, [refreshData, clientDataLastUpdatedDate, updateLegalEntitiesData])

  const legalEntityIds = matchedLegalEntities?.map((legalEntity) => legalEntity.id)
  const { data: employeeGroupsData, loading: employeeGroupsLoading } = useQuery(
    AllEmployeeGroupsDocument,
    {
      variables: {
        legalEntityIds: [...legalEntityIds],
      },
    }
  )
  const employeeGroups = employeeGroupsData?.employees.employeeGroups ?? []
  const employeeGroupsByLegalEntity = groupBy(
    employeeGroups,
    (employeeGroup) => employeeGroup.legalEntityId
  )

  if (employeeGroupsLoading) {
    return <Spinner />
  }
  return (
    <Box flexDirection="row" gap="s96">
      <Box gap="s24">
        <Title
          title={i18n.t(
            'app_store_v2.app_integrations.payroll_integrations_datev_lauds.settings.steps.business_unit.title'
          )}
          description={i18n.t(
            'app_store_v2.app_integrations.payroll_integrations_datev_lauds.settings.steps.business_unit.description'
          )}
        />

        {!refreshData && !isError && (
          <SettingsInfoBanner
            backgroundColor="viridian100"
            title={settingsRefreshBannerTranslations().refresh_banner_title}
            icon="lightbulb"
            iconColor="viridian1000"
            description={
              settingsRefreshBannerTranslations(data.answers.client_data_last_updated_at)
                .refresh_banner_description
            }
            showBanner={() => {
              setRefreshData(true)
              SetIsError(false)
            }}
            buttonText={settingsRefreshBannerTranslations().refresh_banner_button_text}
          />
        )}

        {refreshData && !isError && (
          <SettingsInfoBanner
            backgroundColor="viridian100"
            title={settingsRefreshBannerTranslations().retrieving_data_banner_title}
            icon="spinner"
            iconColor="viridian1000"
            description={settingsRefreshBannerTranslations().retrieving_data_banner_description}
          />
        )}
        {isError && (
          <SettingsInfoBanner
            backgroundColor="radicalRed200"
            title={settingsRefreshBannerTranslations().error_banner_title}
            icon="alert-circle-line"
            iconColor="radicalRed1000"
            description={settingsRefreshBannerTranslations().error_banner_description}
            showBanner={() => {
              setRefreshData(true)
              SetIsError(false)
            }}
            buttonText={settingsRefreshBannerTranslations().error_banner_button_text}
          />
        )}

        <Box justifyContent="flexStart" flexDirection="column" gap="s32">
          {matchedLegalEntities?.map((legalEntity) => {
            const legalEntityEmployeeGroups = employeeGroupsByLegalEntity[legalEntity.id] || []
            if (!legalEntityEmployeeGroups || legalEntityEmployeeGroups.length === 0) return null
            const legalEntityKey = `legal_entity_${legalEntity.id}`

            return (
              <BusinessUnitsByLegalEntity
                data={data}
                key={legalEntityKey}
                legalEntity={legalEntity}
                employeeGroups={legalEntityEmployeeGroups}
                onSelect={(employeeGroupId, value, _label) =>
                  onBusinessUnitChanged(employeeGroupId, value.toString())
                }
                isRefreshData={refreshData}
                isError={isError}
              />
            )
          })}
        </Box>
      </Box>
    </Box>
  )
}

export const businessUnitsStep = (): WizardStep<DatevWizardData> => ({
  title: i18n.t(
    'app_store_v2.app_integrations.payroll_integrations_datev_lauds.settings.steps.business_unit.wizard_title'
  ),
  nextText: i18n.t('common.next'),
  render: (props) => <BusinessUnitsStep {...props} />,
  isCompleted: (data: DatevWizardData) => {
    const employeeGroupKeys = Object.keys(data.answers).filter((key) =>
      /^employee_group_\d+$/.test(key)
    )

    return employeeGroupKeys.length > 0 && employeeGroupKeys.some((key) => !!data.answers[key])
  },
})
